<template>
  <div class="braze-content-cards-list">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner" />
      <p>Loading content...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && displayedCards.length === 0" class="empty-state">
      <p>{{ emptyMessage }}</p>
    </div>

    <!-- Cards Grid/List/Carousel -->
    <div v-else-if="layout === 'carousel'" class="cards-wrapper-carousel">
      <div class="relative">
        <div
          class="braze-carousel-button-prev absolute left-4 top-[50%] -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-lg cursor-pointer transition-all hover:scale-110"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
        
        <nuxt-error-boundary @error="() => {}">
          <Swiper
            :modules="[SwiperNavigation, SwiperPagination]"
            :slides-per-view="1"
            :space-between="16"
            :loop="false"
            :navigation="{
              enabled: true,
              disabledClass: '!bg-gray-10 !text-gray-30 !cursor-not-allowed',
              nextEl: '.braze-carousel-button-next',
              prevEl: '.braze-carousel-button-prev'
            }"
          >

            <SwiperSlide
              v-for="card in displayedCards"
              :key="card.id"
            >
              <BrazeContentCard
                :card="card"
                @click="handleCardClick"
                @dismiss="handleCardDismiss"
              />
            </SwiperSlide>
          </Swiper>
        </nuxt-error-boundary>

        <div
          class="braze-carousel-button-next absolute right-4 top-[50%] -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-lg cursor-pointer transition-all hover:scale-110"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Grid/List Layout -->
    <div v-else class="cards-wrapper">
      <div :class="['cards-container', layout]">
        <BrazeContentCard
          v-for="card in displayedCards"
          :key="card.id"
          :card="card"
          @click="handleCardClick"
          @dismiss="handleCardDismiss"
        />
      </div>
    </div>

    <!-- Refresh Button -->
    <button
      v-if="showRefreshButton && !loading"
      class="refresh-btn"
      @click="refreshCards"
    >
      🔄 Refresh
    </button>

    <!-- Debug Info (only in dev) -->
    <div v-if="showDebug && isDev" class="debug-info">
      <details>
        <summary>🐛 Debug Info ({{ displayedCards.length }} cards)</summary>
        <pre>{{ debugInfo }}</pre>
      </details>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ContentCard, ContentCardType, ContentCardFilters, CardDisplayType, ProductType, PlatformType } from '~/types/brazeContentCards'

const props = withDefaults(defineProps<{
  // Filter options
  type?: ContentCardType
  category?: string
  tag?: string
  onlyUnviewed?: boolean
  onlyActive?: boolean
  minPriority?: number
  
  // Advanced filter options
  cardDisplayType?: CardDisplayType
  productType?: ProductType
  brand?: string
  minPrice?: number
  maxPrice?: number
  platform?: PlatformType
  
  // Display options
  layout?: 'grid' | 'list' | 'carousel'
  maxCards?: number
  showRefreshButton?: boolean
  emptyMessage?: string
  
  // Debug
  showDebug?: boolean
}>(), {
  type: undefined,
  category: undefined,
  tag: undefined,
  onlyUnviewed: false,
  minPriority: undefined,
  cardDisplayType: undefined,
  productType: undefined,
  brand: undefined,
  minPrice: undefined,
  maxPrice: undefined,
  platform: undefined,
  layout: 'grid',
  maxCards: undefined,
  onlyActive: true,
  emptyMessage: 'No content available',
  showRefreshButton: false,
  showDebug: false
})

const emit = defineEmits<{
  cardClick: [card: ContentCard]
  cardDismiss: [card: ContentCard]
  cardsLoaded: [cards: ContentCard[]]
}>()

const {
  cards,
  loading,
  fetchCards,
  filterCards
} = useBrazeContentCards()

// Check if in development
const isDev = import.meta.env.DEV

// Computed: Filtered and displayed cards
const displayedCards = computed(() => {
  const filters: ContentCardFilters = {
    type: props.type,
    category: props.category,
    tag: props.tag,
    onlyUnviewed: props.onlyUnviewed,
    onlyActive: props.onlyActive,
    minPriority: props.minPriority,
    // Advanced filters
    cardDisplayType: props.cardDisplayType,
    productType: props.productType,
    brand: props.brand,
    minPrice: props.minPrice,
    maxPrice: props.maxPrice,
    platform: props.platform
  }

  let filtered = filterCards(filters)

  // Limit number of cards if specified
  if (props.maxCards && props.maxCards > 0) {
    filtered = filtered.slice(0, props.maxCards)
  }

  return filtered
})

// Debug info
const debugInfo = computed(() => {
  return {
    totalCards: cards.value.length,
    displayedCards: displayedCards.value.length,
    filters: {
      type: props.type,
      category: props.category,
      tag: props.tag,
      onlyUnviewed: props.onlyUnviewed,
      onlyActive: props.onlyActive,
      minPriority: props.minPriority
    },
    cards: displayedCards.value.map(c => ({
      id: c.id.substring(0, 8),
      type: c.type,
      title: c.title,
      priority: c.priority,
      viewed: c.viewed,
      dismissed: c.dismissed,
      imageUrl: c.imageUrl,
      imageForMobile: c.imageForMobile,
      imageForDesktop: c.imageForDesktop,
      categories: c.categories,
      extras: c.extras
    }))
  }
})

// Handle card click
const handleCardClick = (card: ContentCard) => {
  emit('cardClick', card)
}

// Handle card dismiss
const handleCardDismiss = (card: ContentCard) => {
  emit('cardDismiss', card)
}

// Refresh cards
const refreshCards = async () => {
  await fetchCards(true)
  emit('cardsLoaded', displayedCards.value)
}

// Initial fetch on mount
onMounted(async () => {
  if (cards.value.length === 0) {
    await fetchCards(false)
  }
  emit('cardsLoaded', displayedCards.value)
})

// Watch for cards changes
watch(() => displayedCards.value, (newCards) => {
  emit('cardsLoaded', newCards)
})
</script>

<style scoped>
.braze-content-cards-list {
  width: 100%;
  position: relative;
}

/* Cards Wrapper */
.cards-wrapper {
  position: relative;
  width: 100%;
}

/* Carousel Wrapper for Swiper */
.cards-wrapper-carousel {
  width: 100%;
  position: relative;
}

.cards-wrapper-carousel :deep(.swiper) {
  width: 100%;
  padding: 0;
}

.cards-wrapper-carousel :deep(.swiper-slide) {
  width: 100%;
  height: auto;
}

/* Navigation Buttons */
.braze-carousel-button-prev,
.braze-carousel-button-next {
  color: #1a1a1a;
}

.braze-carousel-button-prev:hover,
.braze-carousel-button-next:hover {
  transform: translateY(-50%) scale(1.1);
}

.braze-carousel-button-prev:active,
.braze-carousel-button-next:active {
  transform: translateY(-50%) scale(1.05);
}

/* Responsive Navigation */
@media (max-width: 768px) {
  .braze-carousel-button-prev,
  .braze-carousel-button-next {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(4px);
  }

  .braze-carousel-button-prev svg,
  .braze-carousel-button-next svg {
    width: 20px;
    height: 20px;
  }
}

/* Hide navigation buttons on mobile (touch swipe works better) */
@media (max-width: 479px) {
  .braze-carousel-button-prev,
  .braze-carousel-button-next {
    display: none !important;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
}

/* Cards Container - Grid Layout */
.cards-container.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px 0;
}

/* Cards Container - List Layout */
.cards-container.list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
}

/* Refresh Button */
.refresh-btn {
  margin-top: 20px;
  padding: 10px 20px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover {
  background: #e5e5e5;
}

/* Debug Info */
.debug-info {
  margin-top: 20px;
  padding: 16px;
  background: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 12px;
}

.debug-info summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 8px;
}

.debug-info pre {
  background: white;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 8px 0 0 0;
}

/* Responsive Grid & Carousel */
@media (max-width: 1023px) and (min-width: 768px) {
  .cards-container.grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 767px) and (min-width: 480px) {
  .cards-container.grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px 0;
  }

  .cards-container.list {
    gap: 16px;
    padding: 16px 0;
  }
}

@media (max-width: 479px) {
  .cards-container.grid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px 0;
  }

  .cards-container.list {
    gap: 12px;
    padding: 12px 0;
  }
}
</style>
